/**
 * Writing about a drill — the shared surface.
 *
 * Five ways to make a drill yours. The first three transform it (harder,
 * easier, different); the last two are context — what you say while it runs,
 * and when you'd reach for it. None of them need us to have watched the
 * video, which is what lets this work across 800+ drills without anyone
 * authoring per-drill content.
 *
 * One component, two homes: inside a Learning Lab session, and on the drill
 * card anywhere in the library. Same five fields, same `drill_variations`
 * rows, same weights — so a cue written while browsing is the same object as
 * one written in a session, and neither surface has to know about the other.
 *
 * Rep logging is deliberately best-effort. lab_log_rep is behind lab_gate
 * while the Lab is in review, so a coach writing from the library today saves
 * real work that simply doesn't score yet. Losing the write because the
 * scoring isn't open would be the wrong trade.
 */
import React, { useState } from "react";
import Sheet from "../shared/Sheet";
import { X } from "lucide-react";
import { GOLD } from "../constants/theme";
import { supabase } from "../../supabaseClient";
import { bamToast } from "../shared/dialog";

export const VARIATION_FIELDS = {
  // One plain question each. These sit beside the video as a list, so they
  // have to read at a glance; the longer version of each lives in the sheet.
  progression: { label: "Make it harder", chip: "Harder",
    hint: "How would you make this harder?" },
  regression:  { label: "Make it easier", chip: "Easier",
    hint: "How would you make this easier?" },
  remix:       { label: "Remix it", chip: "Remix",
    hint: "How else could you use this drill?" },
  cues:        { label: "My cues", chip: "Cues",
    hint: "What would you say while they run it?" },
  usage:       { label: "When I'd use it", chip: "When",
    hint: "When would you use this?" },
};
export const VARIATION_ORDER = ["progression", "regression", "remix", "cues", "usage"];

/** What this coach has already written on a drill: { kind: body }. */
export async function loadMyVariations(drillId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return {};
    const { data, error } = await supabase
      .from("drill_variations")
      .select("kind, body")
      .eq("user_id", user.id)
      .eq("drill_id", String(drillId));
    if (error) throw error;
    return (data || []).reduce((m, r) => { m[r.kind] = r.body; return m; }, {});
  } catch { return {}; }
}

export function DrillVariationSheet({ C, drill, kind, existing = "", onClose, onSaved }) {
  const [body, setBody] = useState(existing || "");
  const [busy, setBusy] = useState(false);
  const f = VARIATION_FIELDS[kind] || { label: "Your note", hint: "One line is plenty. This becomes yours." };

  const save = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      // One row per coach per drill per kind — editing replaces rather than
      // stacking, so coming back to fix a cue doesn't leave two versions.
      const { error } = await supabase.from("drill_variations").upsert(
        { user_id: user.id, drill_id: String(drill.id), kind, body: body.trim() },
        { onConflict: "user_id,drill_id,kind" });
      if (error) throw error;
      // Scoring is best-effort: the write above is the thing that matters.
      // .then(null, ...), not .catch: the client's calls have no .catch, so that threw after the save worked and the
      // coach saw the error in code (Coleman, 2026-10-29, the iPhone app).
      supabase.rpc("lab_log_rep", {
        p_kind: "variation", p_item_type: "drill", p_item_id: String(drill.id),
      }).then(null, () => {});
      onSaved?.(kind, body.trim());
      onClose();
      bamToast("Saved to your library.");
    } catch (e) {
      bamToast(e?.message || "Couldn't save that.", { tone: "error" });
    } finally { setBusy(false); }
  };

  return (
    <Sheet C={C} onClose={onClose} label={f.label}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2,
            textTransform: "uppercase", color: GOLD }}>{f.label}</span>
          <button className="btn" onClick={onClose} style={{ marginLeft: "auto",
            background: "transparent", border: "none", color: C.textDim, cursor: "pointer" }}>
            <X size={16}/>
          </button>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 3 }}>{drill.title}</div>
        <div style={{ fontSize: 12, color: C.textDim, marginBottom: 11, lineHeight: 1.45 }}>{f.hint}</div>
        <textarea value={body} autoFocus onChange={e => setBody(e.target.value)} rows={4}
          style={{ width: "100%", padding: "11px 13px", borderRadius: 9,
            border: `1px solid ${C.border}`, background: "rgba(255,255,255,0.015)", color: C.text,
            fontSize: 14.5, lineHeight: 1.55, fontFamily: "inherit", resize: "vertical" }}/>
        <button className="btn" onClick={save} disabled={busy || !body.trim()}
          style={{ width: "100%", marginTop: 11, padding: "12px", borderRadius: 9, border: "none",
            background: `linear-gradient(180deg, ${GOLD}, #d4ce8a)`, color: "#111",
            fontWeight: 800, fontSize: 14, fontFamily: "inherit",
            cursor: busy || !body.trim() ? "default" : "pointer",
            opacity: busy || !body.trim() ? 0.45 : 1 }}>
          {busy ? "Saving…" : "Save"}
        </button>
    </Sheet>
  );
}

export default DrillVariationSheet;
